import React, { useEffect, useState } from 'react';
import { taskAPI, projectAPI, userAPI, timeAPI, taskTimeAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import ProjectLeadTasks from './ProjectLeadTasks';
import DocumentUpload from '../components/DocumentUpload';
import { toast } from 'sonner';
import { Play, Square } from 'lucide-react';
import './Tasks.css';

// --- Helper Component for Timer Button ---
const TaskTimerButton = ({ task, activeTimer, startingTask, onStart, onStop, variant = 'list' }) => {
  const [duration, setDuration] = useState('00:00:00');

  // Robust comparison of IDs (handling both populated objects and raw strings)
  const currentTaskId = typeof activeTimer?.task === 'object' ? activeTimer?.task?._id : activeTimer?.task;
  const isActive = currentTaskId && String(currentTaskId) === String(task._id);
  const isStarting = startingTask === task._id;

  // Logic to allow switching: We don't strictly disable if another task is active to allow prompt-switching.
  // We disable if the task is finished/cancelled.
  const isTerminalState = task.status === 'Done' || task.status === 'Cancelled';
  const isDisabled = isStarting || isTerminalState;

  useEffect(() => {
    let interval;
    if (isActive && activeTimer?.startTime) {
      const start = new Date(activeTimer.startTime).getTime();

      const updateTimer = () => {
        const now = Date.now();
        const diff = Math.floor((now - start) / 1000);

        if (diff < 0) {
          setDuration('00:00:00');
          return;
        }

        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        const s = diff % 60;
        setDuration(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
      };

      updateTimer();
      interval = setInterval(updateTimer, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive, activeTimer]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (isActive) {
      onStop(task, e);
    } else {
      onStart(task, e);
    }
  };

  // Class generation
  let btnClass = variant === 'kanban' ? 'btn-start-task-kanban' : 'btn-start-task';
  if (isActive) btnClass += ' active-timer-btn';

  // Label
  const renderLabel = () => {
    if (isStarting) return '...';
    if (isActive) {
      return (
        <span className="timer-display">
          <Square size={14} className="stop-icon" />
          <span className="time-text">{duration}</span>
        </span>
      );
    }
    return (
      <span className="start-display">
        <Play size={14} className="play-icon" />
        <span className="start-text">Start</span>
      </span>
    );
  };

  return (
    <button
      className={btnClass}
      onClick={handleClick}
      disabled={isDisabled}
      title={isTerminalState ? `Task is ${task.status}` : isActive ? 'Stop Timer' : 'Start Timer'}
    >
      {renderLabel()}
    </button>
  );
};

const Tasks = () => {
  const user = useAuthStore((state) => state.user);

  // Redirect Project Lead to specialized component
  if (user?.role === 'Project Lead') {
    return <ProjectLeadTasks />;
  }
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({ status: '', priority: '', type: '', project: '', assignedTo: '', search: '' });
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [formData, setFormData] = useState({
    title: '', description: '', type: '', project: '', assignedTo: '', estimatedHours: '',
    startDate: '', endDate: '', dueDate: '', priority: 'Medium', technology: [], dependencies: [], subtasks: []
  });
  const [comment, setComment] = useState('');
  const [activeTimer, setActiveTimer] = useState(null);
  const [startingTask, setStartingTask] = useState(null);

  const taskTypes = ['Feature Development', 'Bug Fix', 'UI Design', 'API Integration', 'Testing Task', 'Deployment Task', 'Documentation Task'];
  const statuses = ['To Do', 'In Progress', 'In Review', 'Blocked', 'Done', 'Cancelled'];
  const priorities = ['Low', 'Medium', 'High', 'Critical'];

  useEffect(() => {
    loadData();
    checkActiveTimer();
  }, [filters]);

  useEffect(() => {
    checkActiveTimer();
  }, [tasks]); // Re-check when tasks update to ensure sync

  const checkActiveTimer = async () => {
    try {
      const res = await taskTimeAPI.getActiveTask();
      if (res.data?.success && res.data?.data) {
        setActiveTimer(res.data.data);
      } else {
        setActiveTimer(null);
      }
    } catch (error) {
      console.error('Failed to check active timer:', error);
      setActiveTimer(null);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksRes, projectsRes, usersRes] = await Promise.all([
        taskAPI.getAll(filters),
        projectAPI.getAll(),
        userAPI.getAll()
      ]);
      setTasks(tasksRes.data?.data || []);
      setProjects(projectsRes.data?.data || []);
      setUsers(usersRes.data?.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await taskAPI.create({
        ...formData,
        technology: formData.technology.filter(t => t),
        subtasks: formData.subtasks.filter(s => s.title)
      });
      toast.success('Task created successfully');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create task');
    }
  };

  const handleUpdateTask = async (id, updates) => {
    try {
      await taskAPI.update(id, updates);
      loadData();
      if (selectedTask?._id === id) {
        const res = await taskAPI.getById(id);
        setSelectedTask(res.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await taskAPI.delete(id);
      toast.success('Task deleted successfully');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete task');
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    try {
      await taskAPI.addComment(selectedTask._id, { comment });
      setComment('');
      const res = await taskAPI.getById(selectedTask._id);
      setSelectedTask(res.data.data);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleSubtaskToggle = async (subtaskId, completed) => {
    try {
      await taskAPI.updateSubtask(selectedTask._id, { subtaskId, completed });
      const res = await taskAPI.getById(selectedTask._id);
      setSelectedTask(res.data.data);
    } catch (error) {
      toast.error('Failed to update subtask');
    }
  };

  const handleBulkUpdate = async (updates) => {
    if (selectedTasks.length === 0) return;
    try {
      await taskAPI.bulkUpdate({ taskIds: selectedTasks, updates });
      toast.success('Tasks updated successfully');
      setSelectedTasks([]);
      loadData();
    } catch (error) {
      toast.error('Failed to update tasks');
    }
  };

  const openTaskDetail = async (task) => {
    try {
      const res = await taskAPI.getById(task._id);
      setSelectedTask(res.data.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load task details');
    }
  };

  const handleStartTask = async (task, e) => {
    if (e) e.stopPropagation();

    // Switch logic
    if (activeTimer) {
      if (!window.confirm(`Task "${activeTimer.task?.title || 'Unknown'}" is currently running. Stop it and start this one?`)) {
        return;
      }
    }

    if (task.status === 'Done' || task.status === 'Cancelled') {
      toast.error('Cannot start a completed or cancelled task');
      return;
    }

    try {
      setStartingTask(task._id);
      const res = await taskTimeAPI.startTimer({
        taskId: task._id,
        projectId: task.project?._id || task.project
      });

      // Optimistic Status Update
      if (task.status === 'To Do') {
        const newStatus = 'In Progress';
        setTasks(prev => prev.map(t => t._id === task._id ? { ...t, status: newStatus } : t));
        if (selectedTask && selectedTask._id === task._id) {
          setSelectedTask({ ...selectedTask, status: newStatus });
        }
      }

      toast.success('Task timer started!');

      // Update active timer immediately from response if available, or fetch it
      if (res.data?.data) {
        setActiveTimer(res.data.data);
      } else {
        await checkActiveTimer();
      }

      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start task timer');
    } finally {
      setStartingTask(null);
    }
  };

  const handleStopTask = async (task, e) => {
    if (e) e.stopPropagation();
    try {
      setStartingTask(task._id);
      await taskTimeAPI.stopTimer({ taskId: task._id });
      toast.success('Task timer stopped.');
      setActiveTimer(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to stop timer');
    } finally {
      setStartingTask(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', type: '', project: '', assignedTo: '', estimatedHours: '',
      startDate: '', endDate: '', dueDate: '', priority: 'Medium', technology: [], dependencies: [], subtasks: []
    });
  };

  const addTechnology = () => setFormData({ ...formData, technology: [...formData.technology, ''] });
  const updateTechnology = (index, value) => {
    const tech = [...formData.technology];
    tech[index] = value;
    setFormData({ ...formData, technology: tech });
  };
  const removeTechnology = (index) => {
    const tech = formData.technology.filter((_, i) => i !== index);
    setFormData({ ...formData, technology: tech });
  };

  const addSubtask = () => setFormData({ ...formData, subtasks: [...formData.subtasks, { title: '' }] });
  const updateSubtask = (index, value) => {
    const subtasks = [...formData.subtasks];
    subtasks[index].title = value;
    setFormData({ ...formData, subtasks });
  };
  const removeSubtask = (index) => {
    const subtasks = formData.subtasks.filter((_, i) => i !== index);
    setFormData({ ...formData, subtasks });
  };

  const canManageTasks = ['Project Manager', 'Project Lead', 'Operations Head'].includes(user?.role);

  const groupedTasks = {
    'To Do': (tasks || []).filter(t => t?.status === 'To Do'),
    'In Progress': (tasks || []).filter(t => t?.status === 'In Progress'),
    'In Review': (tasks || []).filter(t => t?.status === 'In Review'),
    'Done': (tasks || []).filter(t => t?.status === 'Done')
  };

  return (
    <Layout>
      <div className="tasks-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Tasks</h1>
            <p className="page-subtitle">Manage development tasks and assignments</p>
          </div>
          <div className="header-actions">
            <div className="view-toggle">
              <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>List</button>
              <button className={viewMode === 'kanban' ? 'active' : ''} onClick={() => setViewMode('kanban')}>Kanban</button>
            </div>
            {canManageTasks && (
              <button className="btn-create" onClick={() => setShowCreateModal(true)}>+ Create Task</button>
            )}
          </div>
        </div>

        <div className="filters-bar">
          <input
            type="text"
            placeholder="Search tasks..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="search-input"
          />
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All Status</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
            <option value="">All Priority</option>
            {priorities.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">All Types</option>
            {taskTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filters.project} onChange={(e) => setFilters({ ...filters, project: e.target.value })}>
            <option value="">All Projects</option>
            {projects?.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
          {canManageTasks && (
            <select value={filters.assignedTo} onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}>
              <option value="">All Assignees</option>
              {users?.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
            </select>
          )}
        </div>

        {selectedTasks.length > 0 && canManageTasks && (
          <div className="bulk-actions">
            <span>{selectedTasks.length} selected</span>
            <button onClick={() => handleBulkUpdate({ status: 'In Progress' })}>Mark In Progress</button>
            <button onClick={() => handleBulkUpdate({ status: 'Done' })}>Mark Done</button>
            <button onClick={() => setSelectedTasks([])}>Clear</button>
          </div>
        )}

        {loading ? (
          <div className="loading-state">Loading tasks...</div>
        ) : viewMode === 'list' ? (
          <div className="tasks-list">
            {tasks.length === 0 ? (
              <div className="empty-state">No tasks found</div>
            ) : (
              tasks.map(task => (
                <div key={task._id} className="task-item" onClick={() => openTaskDetail(task)}>
                  {canManageTasks && (
                    <input
                      type="checkbox"
                      checked={selectedTasks.includes(task._id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSelectedTasks(prev =>
                          e.target.checked ? [...prev, task._id] : prev.filter(id => id !== task._id)
                        );
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <div className="task-content">
                    <div className="task-header">
                      <h3>{task.title || 'Untitled Task'}</h3>
                      <div className="task-badges">
                        <span className={`badge status-${(task.status || 'To Do').toLowerCase().replace(' ', '-')}`}>{task.status || 'To Do'}</span>
                        <span className={`badge priority-${(task.priority || 'Medium').toLowerCase()}`}>{task.priority || 'Medium'}</span>
                        {/* Task Timer Button - List View - Integrated into badges area for cleaner alignment */}
                        <TaskTimerButton
                          task={task}
                          activeTimer={activeTimer}
                          startingTask={startingTask}
                          onStart={handleStartTask}
                          onStop={handleStopTask}
                          variant="list"
                        />
                      </div>
                    </div>
                    <p className="task-description">{task.description}</p>
                    <div className="task-meta">
                      <span className="task-type">{task.type || 'Task'}</span>
                      <span>📁 {task.project?.name || 'No Project'}</span>
                      <span>👤 {task.assignedTo?.name || 'Unassigned'}</span>
                      <span>⏱️ {task.estimatedHours || 0}h</span>
                      {task.dueDate && <span className={task.isOverdue ? 'overdue' : ''}>📅 {new Date(task.dueDate).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="kanban-board">
            {Object.entries(groupedTasks).map(([status, statusTasks]) => (
              <div key={status} className="kanban-column">
                <div className="column-header">
                  <h3>{status}</h3>
                  <span className="count">{statusTasks.length}</span>
                </div>
                <div className="column-tasks">
                  {statusTasks.map(task => (
                    <div key={task._id} className="kanban-card" onClick={() => openTaskDetail(task)}>
                      <h4>{task.title || 'Untitled'}</h4>
                      <p>{task.description}</p>
                      <div className="card-footer">
                        <span className={`priority-${(task.priority || 'Medium').toLowerCase()}`}>{task.priority || 'Medium'}</span>
                        <span>{task.assignedTo?.name || 'Unassigned'}</span>
                      </div>

                      {/* Task Timer Button - Kanban View */}
                      <TaskTimerButton
                        task={task}
                        activeTimer={activeTimer}
                        startingTask={startingTask}
                        onStart={handleStartTask}
                        onStop={handleStopTask}
                        variant="kanban"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Create New Task</h2>
                <button className="btn-close" onClick={() => setShowCreateModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleCreateTask} className="task-form">
                <div className="form-group">
                  <label>Title *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type *</label>
                    <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} required>
                      <option value="">Select Type</option>
                      {taskTypes?.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                      {priorities?.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Project *</label>
                    <select value={formData.project} onChange={(e) => setFormData({ ...formData, project: e.target.value })} required>
                      <option value="">Select Project</option>
                      {(projects || [])?.map(p => <option key={p?._id} value={p?._id}>{p?.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Assign To *</label>
                    <select value={formData.assignedTo} onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })} required>
                      <option value="">Select User</option>
                      {(users || []).filter(u => ['Developer', 'QA Engineer', 'UI Engineer', 'Project Lead'].includes(u?.role)).map(u => (
                        <option key={u?._id} value={u?._id}>{u?.name} ({u?.role})</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Estimated Hours *</label>
                    <input type="number" value={formData.estimatedHours} onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input type="date" value={formData.dueDate} onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input type="date" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input type="date" value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Technologies</label>
                  {(formData.technology || []).map((tech, i) => (
                    <div key={i} className="array-input">
                      <input type="text" value={tech} onChange={(e) => updateTechnology(i, e.target.value)} placeholder="e.g., React" />
                      <button type="button" onClick={() => removeTechnology(i)}>Remove</button>
                    </div>
                  ))}
                  <button type="button" onClick={addTechnology} className="btn-add">+ Add Technology</button>
                </div>
                <div className="form-group">
                  <label>Subtasks</label>
                  {(formData.subtasks || [])?.map((st, i) => (
                    <div key={i} className="array-input">
                      <input type="text" value={st?.title} onChange={(e) => updateSubtask(i, e.target.value)} placeholder="Subtask title" />
                      <button type="button" onClick={() => removeSubtask(i)}>Remove</button>
                    </div>
                  ))}
                  <button type="button" onClick={addSubtask} className="btn-add">+ Add Subtask</button>
                </div>
                <div className="form-group">
                  <label>Attachments</label>
                  <DocumentUpload
                    entityType="task"
                    onUploadComplete={(docs) => console.log('Docs uploaded', docs)}
                  />
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button type="submit" className="btn-submit">Create Task</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDetailModal && selectedTask && (
          <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedTask.title}</h2>
                <div className="header-actions-modal" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {/* Task Timer Button - Modal View */}
                  <TaskTimerButton
                    task={selectedTask}
                    activeTimer={activeTimer}
                    startingTask={startingTask}
                    onStart={handleStartTask}
                    onStop={handleStopTask}
                    variant="modal"
                  />
                  <button className="btn-close" onClick={() => setShowDetailModal(false)}>&times;</button>
                </div>
              </div>
              <div className="task-detail">
                <div className="detail-section">
                  <div className="detail-header">
                    <div className="badges">
                      <span className={`badge status-${(selectedTask.status || 'To Do').toLowerCase().replace(' ', '-')}`}>{selectedTask.status || 'To Do'}</span>
                      <span className={`badge priority-${(selectedTask.priority || 'Medium').toLowerCase()}`}>{selectedTask.priority || 'Medium'}</span>
                      <span className="badge">{selectedTask.type || 'Task'}</span>
                    </div>
                    {canManageTasks && (
                      <div className="actions">
                        <select value={selectedTask.status} onChange={(e) => handleUpdateTask(selectedTask._id, { status: e.target.value })}>
                          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button onClick={() => handleDeleteTask(selectedTask._id)} className="btn-delete">Delete</button>
                      </div>
                    )}
                  </div>
                  <p className="description">{selectedTask.description}</p>
                  <div className="detail-grid">
                    <div><strong>Project:</strong> {selectedTask.project?.name}</div>
                    <div><strong>Assigned To:</strong> {selectedTask.assignedTo?.name}</div>
                    <div><strong>Estimated:</strong> {selectedTask.estimatedHours}h</div>
                    <div><strong>Actual:</strong> {selectedTask.actualHours}h</div>
                    {selectedTask.dueDate && <div><strong>Due Date:</strong> {new Date(selectedTask.dueDate).toLocaleDateString()}</div>}
                    {selectedTask.completedAt && <div><strong>Completed:</strong> {new Date(selectedTask.completedAt).toLocaleDateString()}</div>}
                  </div>
                  {(selectedTask.technology || [])?.length > 0 && (
                    <div className="tech-tags">
                      {(selectedTask.technology || [])?.map((t, i) => <span key={i} className="tech-tag">{t}</span>)}
                    </div>
                  )}
                </div>

                {selectedTask.subtasks?.length > 0 && (
                  <div className="detail-section">
                    <h3>Subtasks ({(selectedTask.subtasks || []).filter(s => s.completed).length}/{(selectedTask.subtasks || []).length})</h3>
                    {(selectedTask.subtasks || []).map((st, i) => (
                      <div key={i} className="subtask-item">
                        <input type="checkbox" checked={st.completed} onChange={(e) => handleSubtaskToggle(st._id, e.target.checked)} />
                        <span className={st.completed ? 'completed' : ''}>{st.title}</span>
                      </div>
                    ))}
                  </div>
                )}

                {selectedTask.dependencies?.length > 0 && (
                  <div className="detail-section">
                    <h3>Dependencies</h3>
                    {(selectedTask.dependencies || []).map(dep => (
                      <div key={dep._id} className="dependency-item">
                        <span>{dep?.title}</span>
                        <span className={`badge status-${(dep?.status || '').toLowerCase().replace(' ', '-')}`}>{dep?.status}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="detail-section">
                  <h3>Comments ({selectedTask.comments?.length || 0})</h3>
                  <div className="comments-list">
                    {selectedTask.comments?.map((c, i) => (
                      <div key={i} className="comment">
                        <strong>{c.user?.name}</strong>
                        <span className="comment-date">{new Date(c.createdAt).toLocaleString()}</span>
                        <p>{c.comment}</p>
                      </div>
                    ))}
                  </div>
                  <div className="comment-input">
                    <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." rows="2" />
                    <button onClick={handleAddComment} className="btn-submit">Add Comment</button>
                  </div>
                </div>

                {selectedTask.activityLog?.length > 0 && (
                  <div className="detail-section">
                    <h3>Activity Log</h3>
                    <div className="activity-list">
                      {selectedTask.activityLog.slice(-10).reverse().map((log, i) => (
                        <div key={i} className="activity-item">
                          <span>{log.user?.name}</span>
                          <span>{log.action}</span>
                          <span className="activity-date">{new Date(log.timestamp).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="detail-section">
                  <h3>Documents</h3>
                  <DocumentUpload
                    entityType="task"
                    entityId={selectedTask._id}
                    onUploadComplete={(docs) => console.log('Docs uploaded for existing task', docs)}
                    existingDocuments={selectedTask.documents || []}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Tasks;
